import React from "react";
import { noop } from "@common/helpers/function";
import {
    AccrualDraftCounterpartyState,
    AccrualReviewStatus,
} from "@common/types/financialDocument";
import {
    FilterMultiselect,
    FilterMultiselectOption,
    FilterMultiselectValue,
} from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import { formatFilterLabelText } from "@components/pageHeader/filters/ActiveFilters/formatFilterLabel";
import styles from "../TransactionStatusFilter/TransactionStatusFilter.module.scss";

interface DraftOptionsFilterProps {
    name: string;
    options: FilterMultiselectOption[];
    value: FilterMultiselectValue[] | undefined;
    onChange: (value: FilterMultiselectValue[]) => void;
    searchable?: boolean;
}

export const DraftOptionsFilter: React.FC<DraftOptionsFilterProps> = ({
    name,
    options,
    value,
    onChange,
    searchable = false,
}) => (
    <div className={styles.filter}>
        <FilterMultiselect
            inDropdown={false}
            searchable={searchable}
            options={options}
            value={value ?? []}
            onChange={onChange}
            name={name}
            onBlur={noop}
        />
    </div>
);

const COUNTERPARTY_STATE_LABELS: Record<AccrualDraftCounterpartyState, string> =
    {
        [AccrualDraftCounterpartyState.MATCHED]: "Matched",
        [AccrualDraftCounterpartyState.NEEDS_CONFIRMATION]:
            "Needs confirmation",
        [AccrualDraftCounterpartyState.EMPTY]: "Empty",
    };

const COUNTERPARTY_STATE_OPTIONS = Object.values(
    AccrualDraftCounterpartyState,
).map((state) => ({
    value: state,
    label: COUNTERPARTY_STATE_LABELS[state],
    searchableLabel: COUNTERPARTY_STATE_LABELS[state].toLowerCase(),
}));

const COUNTERPARTY_STATES = new Set<FilterMultiselectValue>(
    Object.values(AccrualDraftCounterpartyState),
);

function isCounterpartyState(
    value: FilterMultiselectValue,
): value is AccrualDraftCounterpartyState {
    return COUNTERPARTY_STATES.has(value);
}

export const DraftCounterpartyFilter: React.FC<{
    value: AccrualDraftCounterpartyState[] | undefined;
    onChange: (states: AccrualDraftCounterpartyState[] | undefined) => void;
}> = ({ value, onChange }) => (
    <DraftOptionsFilter
        name="draftCounterpartyStates"
        options={COUNTERPARTY_STATE_OPTIONS}
        value={value}
        onChange={(selected) => {
            const states = selected.filter(isCounterpartyState);
            onChange(states.length > 0 ? states : undefined);
        }}
    />
);

const ACCRUAL_REVIEW_STATUSES = new Set<FilterMultiselectValue>(
    Object.values(AccrualReviewStatus),
);

export function isAccrualReviewStatus(
    value: FilterMultiselectValue,
): value is AccrualReviewStatus {
    return ACCRUAL_REVIEW_STATUSES.has(value);
}

export const DraftOptionsFilterLabel: React.FC<{
    pluralNoun: string;
    options: FilterMultiselectOption[];
    values: FilterMultiselectValue[] | undefined;
}> = ({ pluralNoun, options, values }) => {
    const labels = options
        .filter((option) => values?.includes(option.value))
        .map((option) =>
            typeof option.label === "string"
                ? option.label
                : String(option.value),
        );
    return <>{formatFilterLabelText(labels, pluralNoun)}</>;
};

export const DraftCounterpartyFilterLabel: React.FC<{
    pluralNoun: string;
    states: AccrualDraftCounterpartyState[] | undefined;
}> = ({ pluralNoun, states }) => (
    <>
        {formatFilterLabelText(
            (states ?? []).map((state) => COUNTERPARTY_STATE_LABELS[state]),
            pluralNoun,
        )}
    </>
);
